/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: useTrialCountdown re-renders on a timer to update the
 * "N days/hours/minutes left" string; the effect intentionally seeds
 * the initial value via setState.
 */
import { useEffect, useState } from "react";
import { formatTrialRemaining } from "@/lib/format";

/**
 * v77.5 — Live trial countdown.
 *
 * Returns the formatted "N days/hours/minutes left" string for the given
 * `trial_end_at` ISO timestamp and re-renders the consuming component
 * once a minute (or every 30 seconds during the final hour) so the
 * countdown actually ticks down instead of sitting stale until the next
 * full page refresh.
 *
 * Returns `null` when there's no timestamp (caller picks the fallback).
 */
export function useTrialCountdown(trialEndIso) {
  const [text, setText] = useState(() => formatTrialRemaining(trialEndIso));

  useEffect(() => {
    if (!trialEndIso) { setText(null); return; }

    const recompute = () => setText(formatTrialRemaining(trialEndIso));
    recompute();

    // Tick more often as we approach the end so "5 minutes left" → "4
    // minutes left" feels responsive. Above 1h the per-minute tick is
    // plenty (the displayed text doesn't change more often anyway).
    const end = new Date(trialEndIso).getTime();
    const intervalMs = (end - Date.now()) < 3600_000 ? 30_000 : 60_000;
    const id = setInterval(recompute, intervalMs);
    return () => clearInterval(id);
  }, [trialEndIso]);

  return text;
}
